import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Custom hook for fetching data with refresh capability
 * @param apiUrl The API endpoint to fetch data from
 * @param initialData Initial data value
 */
function useFetchWithRefresh<T>(apiUrl: string, initialData: T) {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Function to force a data refresh
  const refreshData = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Function to directly update local data without a refresh
  const updateLocalData = useCallback((updater: (prevData: T) => T) => {
    setData(updater);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get(apiUrl);
        console.log(`Fetched data from ${apiUrl}:`, response.data);

        // For debugging purposes, log specific details for the batch API
        if (apiUrl === '/api/batch') {
          if (Array.isArray(response.data)) {
            response.data.forEach((item, index) => {
              console.log(`Batch ${index} raw data:`, JSON.stringify(item).substring(0, 300));
              console.log(`Batch ${index} fields:`,
                'id:', item.id,
                'batch_id:', item.batch_id,
                'batchId:', item.batchId,
                'batch_name:', item.batch_name,
                'batchName:', item.batchName,
                'volume:', item.volume,
                'volume type:', typeof item.volume,
                'volume_unit:', item.volume_unit,
                'volumeUnit:', item.volumeUnit,
                'type:', item.wine_type || item.wineType
              );

              // Detailed volume inspection
              if (item.volume !== undefined) {
                console.log(`Batch ${index} volume detailed:`, {
                  value: item.volume,
                  type: typeof item.volume,
                  isEmptyString: item.volume === '',
                  isZero: item.volume === 0 || item.volume === '0',
                  isNullString: item.volume === 'null',
                  isUndefinedString: item.volume === 'undefined',
                  stringified: String(item.volume)
                });
              }
            });
          }
        }

        setData(response.data);
        setError(null);
      } catch (err: any) {
        console.error(`Error fetching data from ${apiUrl}:`, err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, refreshTrigger]);

  return { data, loading, error, refreshData, updateLocalData, setData };
}

export default useFetchWithRefresh; 